/**
 * Puente entre los contextos de store-ui (Auth/Program) y el SDK de telemetría
 * (@lala/telemetry-sdk). Lee useAuth()/useProgram() y sincroniza:
 *   - actor: identify({ type: 'participant', id }) si logueado, anonymous si no
 *   - baseProperties: { programId, balancePoints } mergeado en todos los eventos
 *   - auto-pageview en cada cambio de ruta (Pages Router)
 *
 * El cliente se crea client-side (SSR-safe). Debe montarse DENTRO de
 * AuthProvider y ProgramProvider.
 */
import { FC, ReactNode, useEffect, useState } from 'react';
import Router from 'next/router';

import { useAuth, useProgram } from 'hooks';
import { getTelemetry, TelemetryProvider } from '~/sdk';
import type { TelemetryClient } from '@lala/telemetry-sdk/web';

interface Props {
  children: ReactNode;
}

export const StoreAnalyticsBridge: FC<Props> = ({ children }) => {
  const { participant, availablePoints, isLoggedIn } = useAuth();
  const { program } = useProgram();
  const [telemetry, setTelemetry] = useState<TelemetryClient | null>(null);

  // Crear el cliente solo en el navegador (init + auto-instrumentación).
  useEffect(() => {
    setTelemetry(getTelemetry());
  }, []);

  // Identidad: participante o anónimo.
  useEffect(() => {
    if (!telemetry) return;
    if (isLoggedIn && participant?.id) {
      telemetry.identify({ type: 'participant', id: String(participant.id) });
    } else {
      telemetry.identify({ type: 'anonymous', id: null });
    }
  }, [telemetry, isLoggedIn, participant?.id]);

  // baseProperties: programId + balancePoints (atPoints) del momento.
  useEffect(() => {
    telemetry?.setBaseProperties({
      programId: program?.id != null ? String(program.id) : undefined,
      balancePoints: availablePoints ?? undefined,
    });
  }, [telemetry, program?.id, availablePoints]);

  // Auto-pageview (Pages Router): el Provider del paquete es agnóstico de router.
  useEffect(() => {
    if (!telemetry) return;
    const emit = (url: string) => telemetry.pageview(url);
    emit(window.location.pathname + window.location.search);
    Router.events.on('routeChangeComplete', emit);
    return () => {
      Router.events.off('routeChangeComplete', emit);
    };
  }, [telemetry]);

  if (!telemetry) return <>{children}</>;
  return <TelemetryProvider client={telemetry}>{children}</TelemetryProvider>;
};
